var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Type } from 'class-transformer';
import { IsArray, IsBoolean, IsEnum, IsInt, IsOptional, IsString, IsUUID, MaxLength, ValidateNested, } from 'class-validator';
import { CustomFormElementType } from '../enums/custom-form-element-type.enum.js';
/**
 * DTO for elements in bulk update. If `id` is provided, the element is updated.
 * If `id` is omitted, a new element is created.
 * Elements not included in the array will be deleted (full replacement).
 */
export class UpdateCustomFormElementItemDto {
    id;
    label;
    index;
    options;
    mandatory;
    type;
    /**
     * Helper text under the question label. `null` clears it — the editor's
     * toggle-off sends exactly that.
     */
    description;
    /**
     * In-field hint. Silently dropped for any type that doesn't render one, so a
     * stale value can never sit in the column (see `supportsCustomFormPlaceholder`).
     */
    placeholder;
}
__decorate([
    IsOptional(),
    IsUUID(),
    __metadata("design:type", String)
], UpdateCustomFormElementItemDto.prototype, "id", void 0);
__decorate([
    IsOptional(),
    IsString(),
    MaxLength(255),
    __metadata("design:type", String)
], UpdateCustomFormElementItemDto.prototype, "label", void 0);
__decorate([
    IsOptional(),
    IsInt(),
    __metadata("design:type", Number)
], UpdateCustomFormElementItemDto.prototype, "index", void 0);
__decorate([
    IsOptional(),
    IsArray(),
    __metadata("design:type", Array)
], UpdateCustomFormElementItemDto.prototype, "options", void 0);
__decorate([
    IsOptional(),
    IsBoolean(),
    __metadata("design:type", Boolean)
], UpdateCustomFormElementItemDto.prototype, "mandatory", void 0);
__decorate([
    IsOptional(),
    IsEnum(CustomFormElementType),
    __metadata("design:type", String)
], UpdateCustomFormElementItemDto.prototype, "type", void 0);
__decorate([
    IsOptional(),
    IsString(),
    MaxLength(500),
    __metadata("design:type", Object)
], UpdateCustomFormElementItemDto.prototype, "description", void 0);
__decorate([
    IsOptional(),
    IsString(),
    MaxLength(255),
    __metadata("design:type", Object)
], UpdateCustomFormElementItemDto.prototype, "placeholder", void 0);
/**
 * A section in a bulk update. If `id` is provided, the section is updated; if
 * omitted, a new section is created. Sections not included are deleted
 * (full replacement). Elements are reconciled form-globally by id (a moved
 * element is reparented into the target section, never delete+recreated), so
 * answer rows survive cross-section moves.
 */
export class UpdateCustomFormSectionItemDto {
    id;
    title;
    index;
    customFormElements;
}
__decorate([
    IsOptional(),
    IsUUID(),
    __metadata("design:type", String)
], UpdateCustomFormSectionItemDto.prototype, "id", void 0);
__decorate([
    IsString(),
    MaxLength(255),
    __metadata("design:type", String)
], UpdateCustomFormSectionItemDto.prototype, "title", void 0);
__decorate([
    IsOptional(),
    IsInt(),
    __metadata("design:type", Number)
], UpdateCustomFormSectionItemDto.prototype, "index", void 0);
__decorate([
    IsArray(),
    IsOptional(),
    ValidateNested({ each: true }),
    Type(() => UpdateCustomFormElementItemDto),
    __metadata("design:type", Array)
], UpdateCustomFormSectionItemDto.prototype, "customFormElements", void 0);
export class UpdateCustomFormDto {
    title;
    description;
    isDefaultForm;
    visibleForPatients;
    users;
    /**
     * Full replacement of form elements. If provided:
     * - Elements with `id` are updated
     * - Elements without `id` are created
     * - Existing elements not in the array are deleted
     *
     * Flat/legacy shape: it names no sections, so the whole list is reconciled as
     * the form's single section. Only accepted for forms that *have* exactly one
     * section — against a multi-section form it is rejected (400) instead of
     * collapsing the structure. Use `customFormSections` for those.
     */
    customFormElements;
    /**
     * Full replacement of the form's sections (section-aware path). When present
     * it takes precedence over the flat `customFormElements`. Sections and their
     * elements are bulk-replaced; elements are reconciled form-globally by id.
     */
    customFormSections;
}
__decorate([
    IsOptional(),
    IsString(),
    MaxLength(255),
    __metadata("design:type", String)
], UpdateCustomFormDto.prototype, "title", void 0);
__decorate([
    IsOptional(),
    IsString(),
    MaxLength(255),
    __metadata("design:type", String)
], UpdateCustomFormDto.prototype, "description", void 0);
__decorate([
    IsOptional(),
    IsBoolean(),
    __metadata("design:type", Boolean)
], UpdateCustomFormDto.prototype, "isDefaultForm", void 0);
__decorate([
    IsOptional(),
    IsBoolean(),
    __metadata("design:type", Boolean)
], UpdateCustomFormDto.prototype, "visibleForPatients", void 0);
__decorate([
    IsArray(),
    IsOptional(),
    __metadata("design:type", Array)
], UpdateCustomFormDto.prototype, "users", void 0);
__decorate([
    IsOptional(),
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => UpdateCustomFormElementItemDto),
    __metadata("design:type", Array)
], UpdateCustomFormDto.prototype, "customFormElements", void 0);
__decorate([
    IsOptional(),
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => UpdateCustomFormSectionItemDto),
    __metadata("design:type", Array)
], UpdateCustomFormDto.prototype, "customFormSections", void 0);
//# sourceMappingURL=update-custom-form.dto.js.map